// src/components/Layout.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  orderBy,
  limit,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import { roleLabel } from '../config/pageAccess';
import styles from '../styles/Layout.module.css';
import MissedCheckoutModal from './MissedCheckoutModal';

import {
  FiGrid, FiBell, FiPlusSquare, FiUsers, FiBriefcase, FiLogOut,
  FiFileText, FiUser, FiBarChart2, FiCalendar,
  FiMessageSquare, FiClipboard, FiCheckSquare, FiPieChart,
  FiTrendingUp, FiEdit, FiLogIn, FiMapPin, FiMenu, FiX, FiClock, FiPackage, FiGlobe,
  FiShare2, FiLayers, FiUserPlus, FiShield
} from 'react-icons/fi';

// Bilingual UI strings. English values must EXACTLY match the previous English so EN is unchanged.
// Arabic is shown when language === 'ar'. navTitles is the breadcrumb map, keyed by first path segment.
const T = {
  en: {
    // Breadcrumb title for the top bar, by first path segment.
    navTitles: {
      dashboard: 'Dashboard', 'growup-chat': 'GrowUp Connect', 'daily-log': 'My Daily Log',
      notifications: 'Notifications', 'client-weekly-work': 'Client Weekly Work', 'work-hours': 'Work Hours',
      tools: 'Tools', task: 'Tasks', 'trainee-tasks-dashboard': 'Trainee Tasks', promotions: 'Promotions',
      clients: 'Clients', campaigns: 'Campaigns', invoices: 'Invoices', employees: 'Employees',
      trainees: 'Trainees', 'team-access': 'Team Access', manager: 'Review Logs', events: 'Manage Events',
      resources: 'Company Resources', social: 'Social Media', profile: 'My Profile',
      'staff-performance': 'Staff Performance', 'team-performance': 'Team Performance',
      'trainee-performance': 'Trainee Report', 'attendance-report': 'Attendance',
      'login-history': 'Login History', 'login-map': 'Login Map', chat: 'AI Chat',
    },
    // Sidebar section headings
    headingGeneral: 'General', headingTools: 'Tools', headingWorkspace: 'Workspace',
    headingManagement: 'Management', headingSocial: 'Social Media', headingReports: 'Reports',
    headingAccount: 'Account',
    // Sidebar nav labels
    navDashboard: 'Dashboard', navGrowupConnect: 'GrowUp Connect', navDailyLog: 'My Daily Log',
    navNotifications: 'Notifications', navClientWeeklyWork: 'Client Weekly Work', navWorkHours: 'Work Hours',
    navTools: 'Tools', navCreateTask: 'Create Task', navTraineeTasks: 'Trainee Tasks',
    navPromotions: 'Promotions', navClients: 'Clients', navClientProfitability: 'Client Profitability',
    navWorkByPerson: 'Work by Person', navCampaigns: 'Campaigns', navInvoices: 'Invoices',
    navCostsRates: 'Costs & Rates', navEmployees: 'Employees', navTrainees: 'Trainees',
    navTeamAccess: 'Team Access', navReviewLogs: 'Review Logs', navManageEvents: 'Manage Events',
    navCompanyResources: 'Company Resources', navSocialDashboard: 'Social Dashboard',
    navContentTasks: 'Content Tasks', navClientSocialPages: 'Client Social Pages',
    navContentCalendar: 'Content Calendar', navContentApprovals: 'Content Approvals',
    navSocialReports: 'Social Reports', navStaffPerformance: 'Staff Performance',
    navTeamPerformance: 'team Performance', navTraineeReport: 'Trainee Report',
    navAttendanceReport: 'Attendance Report', navLoginHistory: 'Login History',
    navLoginMap: 'Login Map', navProfile: 'Profile', navLogout: 'Logout',
    // Titles / aria-labels / misc
    unreadBadgeTitle: 'Unread • Global + DMs', pendingReqTitle: 'Pending Leave + Hours requests',
    toggleMenu: 'Toggle menu', toggleLanguageAria: 'Toggle language', closeMenu: 'Close menu',
    brandLogoAlt: 'Grow Up AM logo', notificationsTitle: 'Notifications', openChatTitle: 'Open Chat',
    loadingDailyLog: 'Checking daily log status...', userFallback: 'User', overview: 'Overview',
    // WhatsApp number modal
    waTitle: 'Enter your WhatsApp number',
    waIntroBefore: 'To finish setting up your account on ',
    waIntroAfter: ', please enter a WhatsApp number.',
    waIntroLine2: "You'll receive notifications for tasks assigned to you directly on this number.",
    waLabel: 'WhatsApp number (with country code)',
    waErrEmpty: 'Please enter the number.',
    waErrFormat: 'Invalid format. Example: +970591234567',
    waErrGeneric: 'An error occurred, please try again.',
    waSaving: 'Saving...', waActivate: 'Activate account',
    waEditLater: 'You can edit the number later from the profile page.',
  },
  ar: {
    navTitles: {
      dashboard: 'لوحة التحكم', 'growup-chat': 'محادثة الفريق', 'daily-log': 'السجل اليومي',
      notifications: 'الإشعارات', 'client-weekly-work': 'أعمال العملاء الأسبوعية', 'work-hours': 'ساعات العمل',
      tools: 'الأدوات', task: 'المهام', 'trainee-tasks-dashboard': 'مهام المتدربين', promotions: 'حاسبة الترقيات',
      clients: 'العملاء', campaigns: 'الحملات', invoices: 'الفواتير', employees: 'الموظفون',
      trainees: 'المتدربون', 'team-access': 'صلاحيات الفريق', manager: 'مراجعة السجلات', events: 'إدارة الفعاليات',
      resources: 'موارد الشركة', social: 'السوشيال ميديا', profile: 'الملف الشخصي',
      'staff-performance': 'أداء الطاقم', 'team-performance': 'أداء الفريق',
      'trainee-performance': 'تقرير المتدربين', 'attendance-report': 'الحضور',
      'login-history': 'سجل الدخول', 'login-map': 'خريطة الدخول', chat: 'محادثة الذكاء الاصطناعي',
    },
    headingGeneral: 'عام', headingTools: 'الأدوات', headingWorkspace: 'مساحة العمل',
    headingManagement: 'الإدارة', headingSocial: 'السوشيال ميديا', headingReports: 'التقارير',
    headingAccount: 'الحساب',
    navDashboard: 'لوحة التحكم', navGrowupConnect: 'محادثة الفريق', navDailyLog: 'السجل اليومي',
    navNotifications: 'الإشعارات', navClientWeeklyWork: 'أعمال العملاء الأسبوعية', navWorkHours: 'ساعات العمل',
    navTools: 'الأدوات', navCreateTask: 'إنشاء مهمة', navTraineeTasks: 'مهام المتدربين',
    navPromotions: 'حاسبة الترقيات', navClients: 'العملاء', navClientProfitability: 'ربحية العملاء',
    navWorkByPerson: 'العمل حسب الشخص', navCampaigns: 'الحملات', navInvoices: 'الفواتير',
    navCostsRates: 'التكاليف والأسعار', navEmployees: 'الموظفون', navTrainees: 'المتدربون',
    navTeamAccess: 'صلاحيات الفريق', navReviewLogs: 'مراجعة السجلات', navManageEvents: 'إدارة الفعاليات',
    navCompanyResources: 'موارد الشركة', navSocialDashboard: 'لوحة السوشيال',
    navContentTasks: 'مهام المحتوى', navClientSocialPages: 'صفحات السوشيال للعملاء',
    navContentCalendar: 'تقويم المحتوى', navContentApprovals: 'موافقات المحتوى',
    navSocialReports: 'تقارير السوشيال', navStaffPerformance: 'أداء الطاقم',
    navTeamPerformance: 'أداء الفريق', navTraineeReport: 'تقرير المتدربين',
    navAttendanceReport: 'تقرير الحضور', navLoginHistory: 'سجل الدخول',
    navLoginMap: 'خريطة الدخول', navProfile: 'الملف الشخصي', navLogout: 'تسجيل الخروج',
    unreadBadgeTitle: 'غير مقروء • العام + الرسائل الخاصة', pendingReqTitle: 'طلبات الإجازات والساعات المعلّقة',
    toggleMenu: 'فتح/إغلاق القائمة', toggleLanguageAria: 'تبديل اللغة', closeMenu: 'إغلاق القائمة',
    brandLogoAlt: 'شعار Grow Up AM', notificationsTitle: 'الإشعارات', openChatTitle: 'فتح المحادثة',
    loadingDailyLog: 'جارٍ التحقق من حالة السجل اليومي...', userFallback: 'مستخدم', overview: 'نظرة عامة',
    waTitle: 'أدخل رقم واتساب الخاص بك',
    waIntroBefore: 'لاستكمال إعداد حسابك في ',
    waIntroAfter: '، يُرجى إدخال رقم واتساب.',
    waIntroLine2: 'ستصلك إشعارات المهام المُسندة إليك مباشرةً على هذا الرقم.',
    waLabel: 'رقم الواتساب (مع رمز الدولة)',
    waErrEmpty: 'الرجاء إدخال الرقم.',
    waErrFormat: 'الصيغة غير صحيحة. مثال: +970591234567',
    waErrGeneric: 'حدث خطأ، حاول مجدداً.',
    waSaving: 'جاري الحفظ...', waActivate: 'تفعيل الحساب',
    waEditLater: 'يمكنك تعديل الرقم لاحقاً من صفحة الملف الشخصي',
  },
};

// --- Role Definitions for Sidebar Visibility ---
const managerRoles = ['Manager', 'Accounts Manager'];
const managerAndSalesRoles = ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'];
const socialManagementRoles = ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'];
const socialContentRoles = ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales', 'Content Creator'];
const campaignAccessRoles = ['Manager', 'Sales Manager', 'Sales', 'Accounts Manager', 'Content Creator'];
const traineeTasksAccessRoles = ['Manager', 'Accounts Manager', 'Trainee'];
const eventCreationRoles = ['Manager', 'Accounts Manager'];
const managerAccessRoles = ['Manager'];
const employeeMenuRoles = ['Manager', 'HR'];
// Client profitability: Account Manager enters work; Manager + Sales Manager see financials.
const clientProfitabilityRoles = ['Manager', 'Sales Manager', 'Accounts Manager'];

const GLOBAL_CHAT_ID = 'global';

// ✅ Use Palestine timezone for “today”
const TZ = 'Asia/Hebron';

const getTodayDateStringTZ = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());

  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}`;
};

// ✅ attendance docId convention (fast path)
const makeAttendanceId = (uid, dateStr) => `${uid}_${dateStr}`;

// ✅ Helper to check if the current user is on vacation today (fast doc read + fallback query).
const isOnVacationToday = async (uid) => {
  if (!uid) return false;
  const today = getTodayDateStringTZ();

  // 1) FAST PATH: direct doc read if your attendance doc IDs are uid_YYYY-MM-DD
  try {
    const attId = makeAttendanceId(uid, today);
    const snap = await getDoc(doc(db, 'attendance', attId));
    if (snap.exists()) {
      const data = snap.data() || {};
      const status = String(data.status || '').toLowerCase();
      if (status === 'vacation' || data?.vacation?.type || data?.vacation?.isVacation) return true;
    }
  } catch {
    // ignore, fallback below
  }

  // 2) FALLBACK: query by userId+date (in case old data schema)
  try {
    const q1 = query(
      collection(db, 'attendance'),
      where('userId', '==', uid),
      where('date', '==', today)
    );
    const snap = await getDocs(q1);
    let onVac = false;
    snap.forEach((d) => {
      const data = d.data() || {};
      const status = String(data.status || '').toLowerCase();
      if (status === 'vacation' || data?.vacation?.type || data?.vacation?.isVacation) onVac = true;
    });
    return onVac;
  } catch {
    return false;
  }
};

const Layout = () => {
  const { currentUser, userRole, isSupervisor, userDoc, pageOverrides, loading: authLoading } = useAuth();
  const { language, toggleLanguage } = useSocialLanguage();
  const t = T[language] || T.ar;

  // A sidebar link shows when the page's per-employee override says so, otherwise by the role
  // default passed in. Keeps the nav in sync with what ProtectedRoute will actually allow.
  const canSee = (pageKey, roleDefault) => {
    const ov = pageOverrides ? pageOverrides[pageKey] : undefined;
    if (ov === true) return true;
    if (ov === false) return false;
    return roleDefault;
  };
  const [waInput, setWaInput] = useState('');
  const [waSaving, setWaSaving] = useState(false);
  const [waError, setWaError] = useState('');
  const [waJustSaved, setWaJustSaved] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingInvoicesCount, setPendingInvoicesCount] = useState(0);

  // ✅ NEW: Pending WorkHours requests count (Leave + Hours)
  const [pendingWorkHoursReqCount, setPendingWorkHoursReqCount] = useState(0);

  // unread counts (computed using chatReads.lastReadAt)
  const [unreadMap, setUnreadMap] = useState({}); // { [chatId]: number }
  const unreadSubsRef = useRef({});               // { [chatId]: {unsubRead, unsubMsgs} }

  const [isLogCheckComplete, setIsLogCheckComplete] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // mobile sidebar

  // Missed checkout reminder
  const [missedRecord, setMissedRecord] = useState(null);   // { date, checkInTime, ... }
  const [missedDocId, setMissedDocId] = useState(null);

  const isMounted = useRef(true);

  // Close sidebar on route change (mobile UX)
  useEffect(() => {
    if (isSidebarOpen) setIsSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      // cleanup chat listeners
      Object.values(unreadSubsRef.current).forEach(({ unsubRead, unsubMsgs }) => {
        unsubRead && unsubRead();
        unsubMsgs && unsubMsgs();
      });
      unreadSubsRef.current = {};
    };
  }, []);

  // Notifications count
  useEffect(() => {
    let unsubscribeNotifications = () => {};
    if (currentUser?.uid) {
      const notificationsRef = collection(db, 'notifications');
      const qNoti = query(
        notificationsRef,
        where('userId', '==', currentUser.uid),
        where('isRead', '==', false)
      );
      unsubscribeNotifications = onSnapshot(
        qNoti,
        (querySnapshot) => {
          if (isMounted.current) setUnreadCount(querySnapshot.size);
        },
        () => {
          if (isMounted.current) setUnreadCount(0);
        }
      );
    } else {
      if (isMounted.current) setUnreadCount(0);
    }
    return () => unsubscribeNotifications();
  }, [currentUser]);

  // Once we've seen a phone number for this user, remember it on this device so the WhatsApp gate
  // never flashes again on reload (Firestore may briefly serve a stale cached doc without it).
  useEffect(() => {
    if (!currentUser?.uid || !userDoc) return;
    const has = !!(userDoc.whatsappNumber || userDoc.phoneNumber || userDoc.phone);
    if (has) {
      try { localStorage.setItem('mam_phone_ok_' + currentUser.uid, '1'); } catch { /* ignore */ }
    }
  }, [currentUser, userDoc]);

  // Pending invoices count
  useEffect(() => {
    let unsubscribePending = () => {};
    if (managerAndSalesRoles.includes(userRole)) {
      const invoicesRef = collection(db, 'invoices');
      const qInv = query(invoicesRef, where('status', '==', 'Pending Approval'));
      unsubscribePending = onSnapshot(
        qInv,
        (snap) => {
          if (isMounted.current) setPendingInvoicesCount(snap.size);
        },
        () => {
          if (isMounted.current) setPendingInvoicesCount(0);
        }
      );
    } else {
      if (isMounted.current) setPendingInvoicesCount(0);
    }
    return () => unsubscribePending();
  }, [userRole]);

  /* =========================
     ✅ NEW: Pending Work Hours Requests (Leave + Hours)
     ========================= */
  useEffect(() => {
    // Only managers see Work Hours, so only they need the listeners
    if (!managerRoles.includes(userRole)) {
      if (isMounted.current) setPendingWorkHoursReqCount(0);
      return () => {};
    }

    // All leave / hours / missed-checkout requests live in the unified `workHoursRequests`
    // collection (the old `leaveRequests`/`hoursRequests` collections are no longer written to).
    const reqRef = collection(db, 'workHoursRequests');
    const qPending = query(reqRef, where('status', '==', 'Pending'));

    const unsub = onSnapshot(
      qPending,
      (snap) => {
        if (isMounted.current) setPendingWorkHoursReqCount(snap.size);
      },
      () => {
        if (isMounted.current) setPendingWorkHoursReqCount(0);
      }
    );

    return () => {
      unsub && unsub();
    };
  }, [userRole]);

  /* =========================
     UNREAD COUNTS (GLOBAL + ALL DMs)
     ========================= */
  const chatReadDocId = useCallback((chatId, uid) => `${chatId}_${uid}`, []);

  const ensureUnreadListener = useCallback((chatId) => {
    const uid = currentUser?.uid;
    if (!uid || !chatId) return;

    // Cleanup previous listeners for this chat
    if (unreadSubsRef.current[chatId]) {
      const { unsubRead, unsubMsgs } = unreadSubsRef.current[chatId];
      unsubRead && unsubRead();
      unsubMsgs && unsubMsgs();
      delete unreadSubsRef.current[chatId];
    }

    const readRef = doc(db, 'chatReads', chatReadDocId(chatId, uid));
    let currentUnsubMsgs = null;

    const unsubRead = onSnapshot(readRef, (readSnap) => {
      const lastReadAt = readSnap.exists() ? (readSnap.data()?.lastReadAt ?? null) : null;

      // (Re)attach messages counter listener
      currentUnsubMsgs && currentUnsubMsgs();

      const msgsRef = collection(db, 'chats', chatId, 'messages');

      const qMsgs = lastReadAt
        ? query(msgsRef, where('createdAt', '>', lastReadAt), orderBy('createdAt', 'asc'), limit(1000))
        : query(msgsRef, orderBy('createdAt', 'asc'), limit(500));

      currentUnsubMsgs = onSnapshot(
        qMsgs,
        (msgSnap) => {
          let count = 0;
          msgSnap.forEach((d) => {
            const m = d.data();
            if (m?.senderId && m.senderId !== uid) count += 1;
          });
          if (isMounted.current) setUnreadMap((prev) => ({ ...prev, [chatId]: count }));
        },
        () => {
          if (isMounted.current) setUnreadMap((prev) => ({ ...prev, [chatId]: 0 }));
        }
      );

      unreadSubsRef.current[chatId] = { unsubRead, unsubMsgs: currentUnsubMsgs };
    });

    unreadSubsRef.current[chatId] = { unsubRead, unsubMsgs: currentUnsubMsgs };
  }, [currentUser, chatReadDocId]);

  // Always track GLOBAL chat unread
  useEffect(() => {
    if (!currentUser?.uid) return;
    ensureUnreadListener(GLOBAL_CHAT_ID);
    return () => {
      const s = unreadSubsRef.current[GLOBAL_CHAT_ID];
      s?.unsubRead && s.unsubRead();
      s?.unsubMsgs && s.unsubMsgs();
      delete unreadSubsRef.current[GLOBAL_CHAT_ID];
    };
  }, [currentUser?.uid, ensureUnreadListener]);

  // Listen to the list of *DM* chats the user is part of and attach unread listeners
  useEffect(() => {
    if (!currentUser?.uid) return () => {};

    const chatsRef = collection(db, 'chats');
    const qDMs = query(
      chatsRef,
      where('type', '==', 'dm'),
      where('members', 'array-contains', currentUser.uid)
    );

    const unsubList = onSnapshot(
      qDMs,
      (chatSnap) => {
        // detach old listeners for DMs (keep global intact)
        Object.keys(unreadSubsRef.current).forEach((cid) => {
          if (cid !== GLOBAL_CHAT_ID) {
            const s = unreadSubsRef.current[cid];
            s?.unsubRead && s.unsubRead();
            s?.unsubMsgs && s.unsubMsgs();
            delete unreadSubsRef.current[cid];
          }
        });

        // Attach listeners for current DM chats
        chatSnap.forEach((chatDoc) => ensureUnreadListener(chatDoc.id));

        // Remove unread entries for chats that no longer exist
        setUnreadMap((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => {
            if (k !== GLOBAL_CHAT_ID && !chatSnap.docs.find((d) => d.id === k)) delete next[k];
          });
          return next;
        });
      },
      () => {
        // on error, clear DM listeners (keep global)
        Object.keys(unreadSubsRef.current).forEach((cid) => {
          if (cid !== GLOBAL_CHAT_ID) {
            const s = unreadSubsRef.current[cid];
            s?.unsubRead && s.unsubRead();
            s?.unsubMsgs && s.unsubMsgs();
            delete unreadSubsRef.current[cid];
          }
        });
      }
    );

    return () => unsubList();
  }, [currentUser?.uid, ensureUnreadListener]);

  // Sum unread across ALL chats (global + all DMs)
  const totalChatUnread = Object.values(unreadMap).reduce((a, b) => a + (b || 0), 0);

  // Mandatory daily log check (except managers) — skips if on vacation (TZ-aware)
  useEffect(() => {
    const checkDailyLog = async () => {
      if (!currentUser || !userRole || location.pathname === '/daily-log' || location.pathname === '/login') {
        if (isMounted.current) setIsLogCheckComplete(true);
        return;
      }

      if (managerRoles.includes(userRole)) {
        if (isMounted.current) setIsLogCheckComplete(true);
        return;
      }

      try {
        const onVacation = await isOnVacationToday(currentUser.uid);
        if (onVacation) {
          if (isMounted.current) setIsLogCheckComplete(true);
          return;
        }

        const todayString = getTodayDateStringTZ();
        const logId = `${currentUser.uid}_${todayString}`;
        const logRef = doc(db, 'dailyLogs', logId);
        const logSnap = await getDoc(logRef);

        if (!logSnap.exists() && isMounted.current) {
          navigate('/daily-log', { replace: true, state: { from: location } });
        }
      } catch {
        // ignore
      } finally {
        if (isMounted.current) setIsLogCheckComplete(true);
      }
    };

    if (!authLoading) checkDailyLog();
  }, [currentUser, userRole, authLoading, navigate, location]);

  // Check for missed checkouts (last 7 days: checkIn exists but no checkOut)
  useEffect(() => {
    if (!currentUser?.uid || authLoading) return;

    const checkMissedCheckout = async () => {
      try {
        const today = getTodayDateStringTZ();

        // Build a date string 7 days ago (TZ-aware)
        const d7 = new Date();
        d7.setDate(d7.getDate() - 7);
        const parts7 = new Intl.DateTimeFormat('en-CA', {
          timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
        }).formatToParts(d7);
        const map7 = Object.fromEntries(parts7.map(p => [p.type, p.value]));
        const sevenDaysAgo = `${map7.year}-${map7.month}-${map7.day}`;

        const q = query(
          collection(db, 'attendance'),
          where('userId', '==', currentUser.uid),
          where('date', '>=', sevenDaysAgo),
          where('date', '<', today)
        );

        const snap = await getDocs(q);
        const incomplete = [];
        snap.forEach(d => {
          const data = d.data();
          if (data.checkInTime && !data.checkOutTime && !data.cancelled) {
            incomplete.push({ docId: d.id, ...data });
          }
        });

        if (incomplete.length > 0 && isMounted.current) {
          // Show the most recent one
          incomplete.sort((a, b) => b.date.localeCompare(a.date));
          setMissedRecord(incomplete[0]);
          setMissedDocId(incomplete[0].docId);
        }
      } catch (err) {
        console.error('[Layout] missed checkout check failed:', err);
      }
    };

    checkMissedCheckout();
  }, [currentUser, authLoading]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      sessionStorage.removeItem('loginActivityRecorded');
      navigate('/login');
    } catch (error) {
      console.error('Logout Failed:', error);
    }
  };

  const getNavLinkClass = ({ isActive }) =>
    isActive ? `${styles.sidebarLink} ${styles.activeLink}` : styles.sidebarLink;

  const handleChatButtonClick = () => navigate('/chat');

  const handleSaveWhatsApp = async () => {
    const trimmed = waInput.trim();
    if (!trimmed) { setWaError(t.waErrEmpty); return; }
    if (!/^\+\d{7,15}$/.test(trimmed)) {
      setWaError(t.waErrFormat);
      return;
    }
    setWaSaving(true);
    setWaError('');
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        whatsappNumber: trimmed,
        updatedAt: serverTimestamp(),
      });
      setWaJustSaved(true); // dismiss immediately even if the live snapshot lags
      try { localStorage.setItem('mam_phone_ok_' + currentUser.uid, '1'); } catch { /* ignore */ }
    } catch {
      setWaError(t.waErrGeneric);
    } finally {
      setWaSaving(false);
    }
  };

  // Block UI while daily-log check is running
  if (!authLoading && !isLogCheckComplete) {
    return <div className={styles.loadingScreen}>{t.loadingDailyLog}</div>;
  }

  // Force WhatsApp number entry before using the app — but only if the user has NO number saved in
  // any field. If they already entered a number (whatsappNumber / phoneNumber / phone), just saved
  // one this session, or were already confirmed on this device before, don't nag them on every open.
  //
  // The `phoneOkStored` flag kills the flash: Firestore can emit a stale cached user doc (without
  // the number) for a split second before the server doc arrives, which briefly flipped this gate
  // on every reload. Once a number was seen for this uid, we remember it locally and never re-prompt.
  const hasAnyPhone = !!(
    userDoc && (userDoc.whatsappNumber || userDoc.phoneNumber || userDoc.phone)
  );
  let phoneOkStored = false;
  try { phoneOkStored = !!(currentUser && localStorage.getItem('mam_phone_ok_' + currentUser.uid) === '1'); } catch { /* ignore */ }
  if (currentUser && userDoc && !hasAnyPhone && !phoneOkStored && !waJustSaved) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 35, 65, 0.70)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 99999, padding: 16,
        fontFamily: 'var(--font-family)',
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          width: '100%', maxWidth: 440,
          boxShadow: '0 32px 80px rgba(0,0,0,0.28)',
          overflow: 'hidden',
          direction: 'rtl',
        }}>
          {/* Brand header — matches sidebar */}
          <div style={{
            background: '#4a90e2',
            padding: '18px 24px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 42, height: 42, borderRadius: 10,
              background: '#fff', color: '#4a90e2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 16, flexShrink: 0,
              boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
            }}>
              AM
            </div>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 20, letterSpacing: 0.3 }}>
              Grow Up AM
            </span>
          </div>

          {/* Body */}
          <div style={{ padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Icon + title */}
            <div style={{ textAlign: 'center', marginBottom: 2 }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: '#e8f8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 14px',
              }}>
                <FiMessageSquare size={28} color="#25d366" />
              </div>
              <h2 style={{ margin: '0 0 8px', color: '#1a2a4a', fontSize: 19, fontWeight: 700 }}>
                {t.waTitle}
              </h2>
              <p style={{ margin: 0, color: '#6b7280', fontSize: 13.5, lineHeight: 1.75 }}>
                {t.waIntroBefore}<strong>Grow Up AM</strong>{t.waIntroAfter}<br />
                {t.waIntroLine2}
              </p>
            </div>

            {/* Input */}
            <div>
              <label style={{
                display: 'block', marginBottom: 6,
                color: '#374151', fontSize: 13, fontWeight: 600,
              }}>
                {t.waLabel}
              </label>
              <input
                type="tel"
                value={waInput}
                onChange={e => { setWaInput(e.target.value); setWaError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSaveWhatsApp()}
                placeholder="+970591234567"
                disabled={waSaving}
                autoFocus
                style={{
                  width: '100%', padding: '11px 14px',
                  borderRadius: 8, boxSizing: 'border-box',
                  border: `1.5px solid ${waError ? '#dc3545' : '#dee2e6'}`,
                  fontSize: 15, color: '#1a2a4a', outline: 'none',
                  direction: 'ltr', textAlign: 'center', letterSpacing: 1.5,
                  background: waSaving ? '#f9fafb' : '#fff',
                  transition: 'border-color 0.18s',
                }}
                onFocus={e => { if (!waError) e.target.style.borderColor = '#4a90e2'; }}
                onBlur={e => { if (!waError) e.target.style.borderColor = '#dee2e6'; }}
              />
              {waError && (
                <p style={{ color: '#dc3545', margin: '5px 0 0', fontSize: 13 }}>{waError}</p>
              )}
            </div>

            {/* Hint chips */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['+970', '+972', '+962', '+966', '+20'].map(code => (
                <button
                  key={code}
                  type="button"
                  onClick={() => { setWaInput(code); setWaError(''); }}
                  style={{
                    padding: '4px 11px', borderRadius: 20,
                    border: '1px solid #dee2e6', background: '#f8f9fa',
                    color: '#4a90e2', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit', direction: 'ltr',
                  }}
                >
                  {code}
                </button>
              ))}
            </div>

            {/* Submit button */}
            <button
              onClick={handleSaveWhatsApp}
              disabled={waSaving}
              style={{
                width: '100%', padding: '12px',
                borderRadius: 8, border: 'none',
                background: waSaving ? '#9ab8de' : '#4a90e2',
                color: '#fff', fontSize: 15, fontWeight: 700,
                cursor: waSaving ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background 0.18s',
                marginTop: 2,
              }}
            >
              {waSaving ? (
                <>
                  <span style={{
                    width: 15, height: 15, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.35)',
                    borderTopColor: '#fff',
                    animation: 'spin 0.7s linear infinite',
                    display: 'inline-block', flexShrink: 0,
                  }} />
                  {t.waSaving}
                </>
              ) : t.waActivate}
            </button>

            <p style={{ color: '#9ca3af', fontSize: 12, margin: 0, textAlign: 'center' }}>
              {t.waEditLater}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.layoutContainer}>
      {/* Missed checkout reminder modal */}
      {missedRecord && missedDocId && (
        <MissedCheckoutModal
          record={missedRecord}
          docId={missedDocId}
          onDone={() => { setMissedRecord(null); setMissedDocId(null); }}
        />
      )}
      {/* Mobile Header */}
      <header className={styles.mobileHeader}>
        <Link to="/" className={styles.mobileBrand}>
          <div
            aria-label={t.brandLogoAlt}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: '#4a90e2',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 14,
              marginRight: 8
            }}
          >
            AM
          </div>
          <span>Grow Up AM</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className={styles.langToggleBtn}
            onClick={toggleLanguage}
            title={language === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
            aria-label={t.toggleLanguageAria}
          >
            {language === 'ar' ? 'EN' : 'ع'}
          </button>
          <button
            className={styles.menuToggle}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label={t.toggleMenu}
          >
            <FiMenu />
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className={styles.sidebarOverlay} onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${isSidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarHeader}>
          <Link to="/" className={styles.sidebarBrand}>
            <div
              aria-label={t.brandLogoAlt}
              className={styles.brandLogo}
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: '#4a90e2',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 16,
                boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                flex: '0 0 40px'
              }}
            >
              AM
            </div>
            <span
              className={styles.brandText}
              style={{ display: 'inline-block', color: '#fff', fontWeight: 800 }}
            >
              Grow Up AM
            </span>
          </Link>
          <button className={styles.closeButton} onClick={() => setIsSidebarOpen(false)} aria-label={t.closeMenu}>
            <FiX />
          </button>
        </div>

        <nav className={styles.sidebarNav}>
          <ul>
            <li className={styles.navHeading}>{t.headingGeneral}</li>

            {canSee('dashboard', userRole !== 'Trainee') && (
              <li>
                <NavLink to="/dashboard" className={getNavLinkClass}>
                  <FiGrid className={styles.navIcon} />
                  <span className={styles.navText}>{t.navDashboard}</span>
                </NavLink>
              </li>
            )}

            {canSee('chat-internal', true) && (
            <li>
              <NavLink to="/growup-chat" className={getNavLinkClass}>
                <FiGlobe className={styles.navIcon} />
                <span className={styles.navText}>{t.navGrowupConnect}</span>
                {totalChatUnread > 0 && (
                  <span
                    className={styles.notificationBadge}
                    title={t.unreadBadgeTitle}
                    style={{ marginLeft: 6 }}
                  >
                    {totalChatUnread > 99 ? '99+' : totalChatUnread}
                  </span>
                )}
              </NavLink>
            </li>
            )}

            {canSee('daily-log', true) && (
            <li>
              <NavLink to="/daily-log" className={getNavLinkClass}>
                <FiEdit className={styles.navIcon} />
                <span className={styles.navText}>{t.navDailyLog}</span>
              </NavLink>
            </li>
            )}

            {canSee('notifications', true) && (
            <li>
              <NavLink to="/notifications" className={getNavLinkClass}>
                <FiBell className={styles.navIcon} />
                <span className={styles.navText}>{t.navNotifications}</span>
                {unreadCount > 0 && (
                  <span className={styles.notificationBadge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </NavLink>
            </li>
            )}

            {canSee('client-weekly-work', ["Manager", "Accounts Manager", "Sales"].includes(userRole)) && (
              <li>
                <NavLink to="/client-weekly-work" className={getNavLinkClass}>
                  <FiClipboard className={styles.navIcon} />
                  <span className={styles.navText}>{t.navClientWeeklyWork}</span>
                </NavLink>
              </li>
            )}

            {/* ✅ Work Hours Management + Pending Requests Badge */}
            {canSee('work-hours', managerRoles.includes(userRole)) && (
              <li>
                <NavLink to="/work-hours" className={getNavLinkClass}>
                  <FiClock className={styles.navIcon} />
                  <span className={styles.navText}>{t.navWorkHours}</span>

                  {pendingWorkHoursReqCount > 0 && (
                    <span className={styles.notificationBadge} title={t.pendingReqTitle}>
                      {pendingWorkHoursReqCount > 99 ? '99+' : pendingWorkHoursReqCount}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            <li className={styles.navHeading}>{t.headingTools}</li>


{canSee('tools', ['Manager', 'Accounts Manager', 'Sales', 'HR'].includes(userRole)) && (
<li>
  <NavLink to="/tools" className={getNavLinkClass}>
    <FiPackage className={styles.navIcon} />
    <span className={styles.navText}>{t.navTools}</span>
  </NavLink>
</li>
)}


            <li className={styles.navHeading}>{t.headingWorkspace}</li>

            {canSee('tasks', userRole !== 'Trainee') && (
              <li>
                <NavLink to="/task/new" className={getNavLinkClass}>
                  <FiPlusSquare className={styles.navIcon} />
                  <span className={styles.navText}>{t.navCreateTask}</span>
                </NavLink>
              </li>
            )}

            {canSee('trainee-tasks', traineeTasksAccessRoles.includes(userRole) || isSupervisor) && (
              <li>
                <NavLink to="/trainee-tasks-dashboard" className={getNavLinkClass}>
                  <FiCheckSquare className={styles.navIcon} />
                  <span className={styles.navText}>{t.navTraineeTasks}</span>
                </NavLink>
              </li>
            )}

            {canSee('promotions', userRole === 'Manager') && (
              <li>
                <NavLink to="/promotions" className={getNavLinkClass}>
                  <FiBarChart2 className={styles.navIcon} />
                  <span className={styles.navText}>{t.navPromotions}</span>
                </NavLink>
              </li>
            )}

            <li className={styles.navHeading}>{t.headingManagement}</li>

            {canSee('clients', managerAndSalesRoles.includes(userRole)) && (
              <li>
                <NavLink to="/clients" className={getNavLinkClass}>
                  <FiBriefcase className={styles.navIcon} />
                  <span className={styles.navText}>{t.navClients}</span>
                </NavLink>
              </li>
            )}

            {canSee('client-profitability', clientProfitabilityRoles.includes(userRole)) && (
              <li>
                <NavLink to="/client-profitability" className={getNavLinkClass}>
                  <FiTrendingUp className={styles.navIcon} />
                  <span className={styles.navText}>{t.navClientProfitability}</span>
                </NavLink>
              </li>
            )}

            {canSee('work-by-person', ['Manager', 'Sales Manager'].includes(userRole)) && (
              <li>
                <NavLink to="/work-by-person" className={getNavLinkClass}>
                  <FiUser className={styles.navIcon} />
                  <span className={styles.navText}>{t.navWorkByPerson}</span>
                </NavLink>
              </li>
            )}

            {canSee('campaigns', campaignAccessRoles.includes(userRole)) && (
              <li>
                <NavLink to="/campaigns" className={getNavLinkClass}>
                  <FiFileText className={styles.navIcon} />
                  <span className={styles.navText}>{t.navCampaigns}</span>
                </NavLink>
              </li>
            )}

            {canSee('invoices', managerAndSalesRoles.includes(userRole)) && (
              <li>
                <NavLink to="/invoices" className={getNavLinkClass}>
                  <FiFileText className={styles.navIcon} />
                  <span className={styles.navText}>{t.navInvoices}</span>
                  {pendingInvoicesCount > 0 && (
                    <span className={styles.notificationBadge}>
                      {pendingInvoicesCount > 9 ? '9+' : pendingInvoicesCount}
                    </span>
                  )}
                </NavLink>
              </li>
            )}

            {canSee('cost-settings', userRole === 'Manager') && (
              <li>
                <NavLink to="/cost-settings" className={getNavLinkClass}>
                  <FiPieChart className={styles.navIcon} />
                  <span className={styles.navText}>{t.navCostsRates}</span>
                </NavLink>
              </li>
            )}

            {canSee('employees', employeeMenuRoles.includes(userRole)) && (
              <li>
                <NavLink to="/employees" className={getNavLinkClass}>
                  <FiUsers className={styles.navIcon} />
                  <span className={styles.navText}>{t.navEmployees}</span>
                </NavLink>
              </li>
            )}

            {canSee('trainees', managerRoles.includes(userRole)) && (
              <li>
                <NavLink to="/trainees" className={getNavLinkClass}>
                  <FiUserPlus className={styles.navIcon} />
                  <span className={styles.navText}>{t.navTrainees}</span>
                </NavLink>
              </li>
            )}

            {managerAccessRoles.includes(userRole) && (
              <li>
                <NavLink to="/team-access" className={getNavLinkClass}>
                  <FiShield className={styles.navIcon} />
                  <span className={styles.navText}>{t.navTeamAccess}</span>
                </NavLink>
              </li>
            )}

            {canSee('manager-daily-logs', managerRoles.includes(userRole)) && (
              <li>
                <NavLink to="/manager/daily-logs" className={getNavLinkClass}>
                  <FiTrendingUp className={styles.navIcon} />
                  <span className={styles.navText}>{t.navReviewLogs}</span>
                </NavLink>
              </li>
            )}

            {canSee('events', eventCreationRoles.includes(userRole)) && (
              <li>
                <NavLink to="/events" className={getNavLinkClass}>
                  <FiCalendar className={styles.navIcon} />
                  <span className={styles.navText}>{t.navManageEvents}</span>
                </NavLink>
              </li>
            )}

            {canSee('resources', eventCreationRoles.includes(userRole)) && (
              <li>
                <NavLink to="/resources" className={getNavLinkClass}>
                  <FiPackage className={styles.navIcon} />
                  <span className={styles.navText}>{t.navCompanyResources}</span>
                </NavLink>
              </li>
            )}

            <li className={styles.navHeading}>{t.headingSocial}</li>

            {canSee('social-dashboard', socialManagementRoles.includes(userRole)) && (
              <li>
                <NavLink to="/social/dashboard" className={getNavLinkClass}>
                  <FiShare2 className={styles.navIcon} />
                  <span className={styles.navText}>{t.navSocialDashboard}</span>
                </NavLink>
              </li>
            )}

            {canSee('social-tasks', socialContentRoles.includes(userRole)) && (
              <li>
                <NavLink to="/social/tasks" className={getNavLinkClass}>
                  <FiLayers className={styles.navIcon} />
                  <span className={styles.navText}>{t.navContentTasks}</span>
                </NavLink>
              </li>
            )}

            {canSee('social-pages', socialManagementRoles.includes(userRole)) && (
              <li>
                <NavLink to="/social/pages" className={getNavLinkClass}>
                  <FiGlobe className={styles.navIcon} />
                  <span className={styles.navText}>{t.navClientSocialPages}</span>
                </NavLink>
              </li>
            )}

            {canSee('social-calendar', socialManagementRoles.includes(userRole)) && (
              <li>
                <NavLink to="/social/calendar" className={getNavLinkClass}>
                  <FiCalendar className={styles.navIcon} />
                  <span className={styles.navText}>{t.navContentCalendar}</span>
                </NavLink>
              </li>
            )}

            {canSee('social-approvals', socialManagementRoles.includes(userRole)) && (
              <li>
                <NavLink to="/social/approvals" className={getNavLinkClass}>
                  <FiCheckSquare className={styles.navIcon} />
                  <span className={styles.navText}>{t.navContentApprovals}</span>
                </NavLink>
              </li>
            )}

            {canSee('social-reports', socialManagementRoles.includes(userRole)) && (
              <li>
                <NavLink to="/social/reports" className={getNavLinkClass}>
                  <FiPieChart className={styles.navIcon} />
                  <span className={styles.navText}>{t.navSocialReports}</span>
                </NavLink>
              </li>
            )}

            <li className={styles.navHeading}>{t.headingReports}</li>

            {canSee('staff-performance', userRole === 'Manager') && (
              <li>
                <NavLink to="/staff-performance" className={getNavLinkClass}>
                  <FiBarChart2 className={styles.navIcon} />
                  <span className={styles.navText}>{t.navStaffPerformance}</span>
                </NavLink>
              </li>
            )}

            {canSee('team-performance', userRole === 'Manager') && (
              <li>
                <NavLink to="/team-performance" className={getNavLinkClass}>
                  <FiBarChart2 className={styles.navIcon} />
                  <span className={styles.navText}>{t.navTeamPerformance}</span>
                </NavLink>
              </li>
            )}

            {canSee('trainee-performance', managerRoles.includes(userRole)) && (
              <li>
                <NavLink to="/trainee-performance" className={getNavLinkClass}>
                  <FiPieChart className={styles.navIcon} />
                  <span className={styles.navText}>{t.navTraineeReport}</span>
                </NavLink>
              </li>
            )}



            {canSee('attendance', userRole && managerRoles.includes(userRole)) && (
              <li>
                <NavLink to="/attendance-report" className={getNavLinkClass}>
                  <FiClock className={styles.navIcon} />
                  <span className={styles.navText}>{t.navAttendanceReport}</span>
                </NavLink>
              </li>
            )}

            {canSee('login-history', userRole && managerAccessRoles.includes(userRole)) && (
              <li>
                <NavLink to="/login-history" className={getNavLinkClass}>
                  <FiLogIn className={styles.navIcon} />
                  <span className={styles.navText}>{t.navLoginHistory}</span>
                </NavLink>
              </li>
            )}

            {canSee('login-map', userRole && managerAccessRoles.includes(userRole)) && (
              <li>
                <NavLink to="/login-map" className={getNavLinkClass}>
                  <FiMapPin className={styles.navIcon} />
                  <span className={styles.navText}>{t.navLoginMap}</span>
                </NavLink>
              </li>
            )}

            <li className={styles.navHeading}>{t.headingAccount}</li>
            <li>
              <NavLink to="/profile" className={getNavLinkClass}>
                <FiUser className={styles.navIcon} />
                <span className={styles.navText}>{t.navProfile}</span>
              </NavLink>
            </li>
          </ul>
        </nav>

        <div className={styles.sidebarFooter}>
          <button onClick={handleLogout} className={styles.logoutButton}>
            <FiLogOut className={styles.navIcon} />
            <span className={styles.navText}>{t.navLogout}</span>
          </button>
        </div>
      </aside>

      <main className={styles.content}>
        {/* Desktop top bar (hidden on mobile, where mobileHeader is shown instead) */}
        <div className={styles.topbar}>
          <div className={styles.crumbs}>
            <FiGrid className={styles.crumbIcon} />
            <span className={styles.crumbMain}>
              {t.navTitles[location.pathname.split('/').filter(Boolean)[0]] || t.overview}
            </span>
            <span className={styles.crumbSep}>/</span>
            <span className={styles.crumbSub}>{t.overview}</span>
          </div>

          <div className={styles.topbarActions}>
            <button
              type="button"
              className={styles.langToggleBtn}
              onClick={toggleLanguage}
              title={language === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
              aria-label={t.toggleLanguageAria}
            >
              {language === 'ar' ? 'EN' : 'ع'}
            </button>
            <NavLink to="/notifications" className={styles.topIconBtn} title={t.notificationsTitle}>
              <FiBell />
              {unreadCount > 0 && (
                <span className={styles.topIconBadge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </NavLink>
            <NavLink to="/profile" className={styles.userChip}>
              <img
                src={currentUser?.photoURL || '/default-avatar.png'}
                alt=""
                className={styles.userChipAvatar}
                onError={(e) => (e.currentTarget.src = '/default-avatar.png')}
              />
              <div className={styles.userChipText}>
                <div className={styles.userChipName}>
                  {userDoc?.name || currentUser?.displayName || currentUser?.email || t.userFallback}
                </div>
                <div className={styles.userChipRole}>{roleLabel(userRole, language)}</div>
              </div>
            </NavLink>
          </div>
        </div>

        <div className={styles.contentInner}>
          <Outlet />
        </div>
      </main>

      <button
        className={styles.chatbotToggler}
        onClick={handleChatButtonClick}
        title={t.openChatTitle}
      >
        <FiMessageSquare />
      </button>
    </div>
  );
};

export default Layout;
